import type { Player } from '../../shared/account';
import type { Drawing } from '../../shared/art/types';
import type { Choice } from '../../shared/word-types';
export type { Point, Stroke, StrokeBrush, StrokeShape, DrawingEvent, Drawing } from '../../shared/art/types';
export type { Word, Choice } from '../../shared/word-types';
export { CHOICE_REFRESH, normalizeAnswer } from '../../shared/word-types';
export type { Player } from '../../shared/account';
export { PALETTES, PALETTE_GROUPS, BASE_COLORS, tierName } from '../../shared/account';
export interface GuessAttempt { answer: string; correct: boolean; t: number }
/**
 * 猜题过程事件：猜词页真实发生的操作，用于给画者完整回放对方怎么猜的。
 * `t` 是相对「首次进入猜词页」的真实毫秒偏移；同一设备重进沿用时间基准，回放不重排间隔。
 * 选中候选字记 `pick`，点「重新选字」记 `clear`（这就是“取消了选择”），提示记 `hint`。
 */
export interface GuessEvent {
  kind: 'pick' | 'clear' | 'type' | 'hint' | 'submit';
  slot?: number; index?: number; text?: string; giveUp?: boolean; t: number;
}
/** 猜题时画作播放器的状态节点，与GuessEvent共用首次进入猜题的时间基准。 */
export interface GuessViewEvent {
  t: number; position: number; speed: number; playing: boolean;
}
/** 一次猜题的完整过程：候选字（含提示前的原始批次）+ 事件流 + 每次提交的结果。 */
export interface GuessProcess {
  id: string; number: number; category?: string; answer: string; letterCount: number;
  correct: boolean; hintUsed: boolean; attempts: number;
  /** 提示后的候选批次（玩家最后看到的）；banksFull 是提示前的原始批次。 */
  banks: string[][]; banksFull: string[][];
  guesses: GuessAttempt[]; events: GuessEvent[]; createdAt: number;
  /** 旧客户端未记录此字段；不能据此推断其重播、倍速或跳过操作。 */
  viewEvents?: GuessViewEvent[];
}
/** 上报事件条数上限：足够记录一次很长的猜题，又不会把回合记录撑大。 */
export const GUESS_EVENT_LIMIT = 300;
/** 真实毫秒偏移的安全整数上限：长时间离开再回来也不能被截到30分钟。 */
export const GUESS_EVENT_MAX_MS = Number.MAX_SAFE_INTEGER;
export type TurnStatus = 'choosing' | 'drawing' | 'reviewing' | 'ready' | 'resolved' | 'rejected';
export interface PublicTurn {
  id: string; sessionId: string; artistId: string; status: TurnStatus; number: number;
  choices?: Choice[]; word?: string; category?: string; reward?: number;
  letterCount?: number; banks?: string[][]; hintUsed?: boolean; refreshes?: number;
  drawing?: Drawing; reason?: string; correct?: boolean; guesses?: GuessAttempt[]; createdAt: number;
  likeCount?: number; likedByMe?: boolean;
  /** 已揭晓且留下了猜题过程时置真：会话视图据此提示「可以回看 TA 怎么猜的」。 */
  hasReplay?: boolean;
}
export interface SessionView {
  mode?: 'draw-guess';
  id: string; members: Pick<Player, 'id' | 'nickname' | 'avatarUrl'>[];
  status: 'inviting' | 'active' | 'closed'; inviteCode?: string; inviteExpiresAt?: number;
  current: PublicTurn; history: PublicTurn[]; solved: number; updatedAt: number;
}
export interface SessionSummary {
  mode?: 'draw-guess';
  id: string; partner: string; status: SessionView['status']; action: 'draw' | 'guess' | 'wait' | 'review' | 'closed';
  solved: number; number: number; updatedAt: number; partnerAvatarUrl?: string; partnerOnline?: boolean;
}
export interface GuessResult { correct: boolean; resolved: boolean; answer?: string; reward?: number; session: SessionView; player: Player }
